import colors from '@sportninja/common/constants/appColors'
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Image, Spin } from 'antd'
import styled from 'styled-components'
import {
  fetchVideographers,
  Videographer,
} from 'src/pages/Game/VideoAssignment/api'
import req from '@sportninja/common/api/request'
import { TeamOfficial } from '@sportninja/common/types/TeamOfficialRoster'

interface VideographerSelectContentProps {
  gameId: string
  rosterId: string
  refreshKey?: number
  officials?: TeamOfficial[]
  onAssigned?: () => void
}

interface VideographerTableRow {
  id: string
  userId: string | null
  displayName: string
  imageUrl: string | null
  positionLabel: string
  isPendingInvite: boolean
}

const PLACEHOLDER_IMAGE = '/images/generic_placeholders/placeholder_player.png'

const Container = styled.div`
  background-color: ${colors.SECONDARY_300};
  color: white;
  padding: 1rem;
  border-radius: 0.5rem;
  max-height: 500px;
  min-height: 500px;
  overflow-y: auto;
`

const Table = styled.table`
  width: 100%;
`

const TableHead = styled.thead`
  text-align: left;
  background-color: ${colors.SECONDARY_200};
  color: ${colors.NEUTRAL_50};
  font-size: 14px;
  line-height: 20px;

  & th {
    padding: 8px;

    &:first-child {
      border-top-left-radius: 8px;
    }
    &:last-child {
      border-top-right-radius: 8px;
    }
  }
`

const TableRow = styled.tr<{ clickable?: boolean; disabled?: boolean }>`
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

  &:nth-child(even) {
    background-color: ${colors.SECONDARY_200};
  }
`

const TableCell = styled.td`
  padding: 8px;
  overflow: hidden;
  color: ${colors.NEUTRAL_0};
  text-overflow: ellipsis;
  font-size: 16px;
  font-weight: 400;
  line-height: 23.2px;
  text-align: center;
  vertical-align: middle;
`

const PlayerImageWrapper = styled.div`
  width: 32px;
  height: 32px;
  margin-right: 8px;
  overflow: hidden;
  border-radius: 50%;
  background-color: ${colors.NEUTRAL_0};
`

const PlayerNameCell = styled.div`
  display: flex;
  align-items: center;
`

const PlayerPositionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const NumberCell = styled(TableCell)`
  text-align: center;
  border-width: 1px;
`

const LoadingContainer = styled(Container)`
  display: flex;
  justify-content: center;
  align-items: center;
`

const playerImageStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
}

const VideographerSelectContent: React.FC<VideographerSelectContentProps> = ({
  gameId,
  rosterId,
  refreshKey,
  officials = [],
  onAssigned,
}) => {
  const [videographers, setVideographers] = useState<Videographer[]>([])
  const [loading, setLoading] = useState(true)
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set())

  const tableRows = useMemo<VideographerTableRow[]>(() => {
    const officialRows = officials.map((official) => {
      const userId = official.user_id || official.id || null
      const displayName =
        `${official.name_first || ''} ${official.name_last || ''}`.trim() ||
        official.name_full ||
        official.email ||
        'Unknown'

      return {
        id: `official_${official.id || userId || displayName}`,
        userId,
        displayName,
        imageUrl: null,
        positionLabel: 'Team Official',
        isPendingInvite: false,
      }
    })

    const videographerRows = videographers.map((videographer) => {
      const userId = videographer.user_id || null
      const displayName =
        `${videographer.name_first || ''} ${
          videographer.name_last || ''
        }`.trim() ||
        videographer.email ||
        'Unknown'
      const rowId =
        videographer.user_id ||
        videographer.invitation_id ||
        videographer.email ||
        displayName

      return {
        id: `videographer_${rowId}`,
        userId,
        displayName,
        imageUrl: videographer.avatar_url || null,
        positionLabel: videographer.user_exists
          ? 'Videographer'
          : 'Videographer (Pending Invitation)',
        isPendingInvite: !videographer.user_exists || !videographer.user_id,
      }
    })

    return [...officialRows, ...videographerRows]
  }, [officials, videographers])

  const loadVideographers = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchVideographers(gameId, rosterId)
      setVideographers(data)
    } catch {
      // Silently fail
    } finally {
      setLoading(false)
    }
  }, [gameId, rosterId])

  useEffect(() => {
    loadVideographers()
  }, [loadVideographers, refreshKey])

  const assignVideographer = useCallback(
    async (userId: string) => {
      setUpdatingIds((prev) => new Set(prev).add(userId))
      let assignmentSucceeded = false

      try {
        await req(
          `/games/${gameId}/rosters/${rosterId}/users/${userId}/attendance`,
          {
            method: 'PUT',
            body: JSON.stringify({
              bringing_video: true,
              is_attending: true,
            }),
            parseJSON: false,
          }
        )
        assignmentSucceeded = true
      } catch {
        // Silently fail
      } finally {
        setUpdatingIds((prev) => {
          const next = new Set(prev)
          next.delete(userId)
          return next
        })
      }

      if (assignmentSucceeded && onAssigned) {
        onAssigned()
      }
    },
    [gameId, rosterId, onAssigned]
  )

  if (loading) {
    return (
      <LoadingContainer>
        <Spin />
      </LoadingContainer>
    )
  }

  if (tableRows.length === 0) {
    return <Container>No videographers or officials assigned yet.</Container>
  }

  return (
    <Container>
      <Table>
        <TableHead>
          <tr>
            <th>#</th>
            <th>Player Name</th>
            <th>Position</th>
          </tr>
        </TableHead>
        <tbody>
          {tableRows.map((row) => {
            const isUpdating = row.userId ? updatingIds.has(row.userId) : false
            const isDisabled = row.isPendingInvite || !row.userId || isUpdating

            return (
              <TableRow
                clickable={!isDisabled}
                disabled={isDisabled}
                onClick={() => {
                  if (!isDisabled && row.userId) {
                    assignVideographer(row.userId)
                  }
                }}
                key={row.id}
              >
                <NumberCell>#</NumberCell>
                <TableCell>
                  <PlayerNameCell>
                    <PlayerImageWrapper>
                      <Image
                        src={row.imageUrl || PLACEHOLDER_IMAGE}
                        alt={row.displayName}
                        preview={false}
                        style={playerImageStyle}
                        fallback={PLACEHOLDER_IMAGE}
                      />
                    </PlayerImageWrapper>
                    {row.displayName}
                  </PlayerNameCell>
                </TableCell>
                <TableCell>
                  <PlayerPositionContainer>
                    <div>{row.positionLabel}</div>
                    {isUpdating ? <Spin size='small' /> : null}
                  </PlayerPositionContainer>
                </TableCell>
              </TableRow>
            )
          })}
        </tbody>
      </Table>
    </Container>
  )
}

export default VideographerSelectContent
